/**
 * Editorial content for /security — transcribed from the design handoff
 * prototype (`design_handoff_security_page/Security v2.dc.html`,
 * `renderVals()`). All of it is hardcoded marketing copy, not live data.
 *
 * Editorial stance (handoff): every claim verifiable — facts, dates and named
 * things; "in progress" never dressed up as certified. Affirmative marks are
 * brand pink, never green; amber is reserved for honest roadmap states; cyan
 * appears only as mono syntax accent.
 */

import type { LucideIcon } from "lucide-react";
import { ArrowRight, Database, LayoutGrid, Lock, ShieldCheck } from "lucide-react";
import { legal } from "@/lib/legal/facts";
import { PURPOSE } from "@/lib/legal/content/en/strings";

/** One hop of the request path — the page's signature section. */
export interface Hop {
  n: string;
  icon: LucideIcon;
  name: string;
  seal: string;
  body: string;
}

export const HOPS: Hop[] = [
  {
    n: "01",
    icon: Lock,
    name: "TLS handshake",
    seal: "sealed · https · auto-issued certs",
    body:
      "Links on your domains are served over HTTPS, with certificates issued and renewed " +
      "automatically — nothing for you to upload, nothing to forget to renew.",
  },
  {
    n: "02",
    icon: ShieldCheck,
    name: "Abuse check",
    seal: "sealed · honeypot · timing · turnstile",
    body:
      "Public forms pass named bot checks — honeypot fields, render-to-submit timing, and a " +
      "Turnstile check on every form that sends email or reaches our team (on the shortener, only " +
      "when a submission looks automated). The redirect itself skips all of it.",
  },
  {
    n: "03",
    icon: Database,
    name: "Lookup",
    seal: "sealed · o(1) · config read-only",
    body:
      "The path resolves with O(1) reads from Redis. The routing service only reads link " +
      "configuration — changes arrive solely through the API, never from the redirect path.",
  },
  {
    n: "04",
    icon: LayoutGrid,
    name: "Tenant boundary",
    seal: "sealed · tenant_id on every row",
    body:
      "Every tenant-owned row carries a tenant_id, and queries are scoped to it at the repository " +
      "layer — isolation is built into the data layer, not left to code review.",
  },
  {
    n: "05",
    icon: ArrowRight,
    name: "Response",
    seal: "sealed · logged vs discarded",
    body:
      "The redirect goes back in milliseconds. Analytics keeps request metadata, but the visitor IP is " +
      "truncated to a /24 IPv4 or /48 IPv6 network prefix before durable storage.",
  },
];

// No latency, region or encryption figure is quoted that we can't show:
// latency lives on the public status page; hosting regions stay neutral
// ("EU-first — visitor IPs anonymised before storage", owner 2026-09-27).
export const CODA = "abuse checks never touch the redirect hot path ↓";

/** Components the public status page monitors (mirrors components/status/data.ts). */
export const MONITORED = [
  { name: "Routing", note: "p50 / p99 · 60 s probe", primary: true },
  { name: "API", note: "uptime" },
  { name: "Dashboard", note: "uptime" },
  { name: "MCP server", note: "uptime" },
  { name: "Email", note: "uptime" },
];

export const INFRA_CHECKS = [
  "99.99% uptime SLA in every Enterprise agreement — the terms are spelled out there",
  "Routing and the write API run as separate services — link config is only read on the redirect path",
  "Public status page with live latency and incident history — see live status at rii.link/status",
];

/** What each kind of data is — no location tags: the privacy policy names
 *  every processor and its country (owner 2026-09-27: region-neutral). */
export const DATA_TYPES = [
  {
    type: "Routing configuration",
    desc: "Paths, destinations, rules — written by the API, read by routing.",
    tag: "api → routing",
  },
  {
    type: "Click events",
    desc: "Country, device, browser, referrer and UTM tags — IP truncated first.",
    tag: "IP /24 · /48",
  },
  {
    type: "Object storage",
    desc: "Generated QR codes and uploaded assets.",
    tag: "processor list ↓",
  },
];

export const SPECS: { k: string; v: string; note?: string }[] = [
  { k: "In transit", v: "HTTPS on rii.link and on your domains — certificates issued and renewed automatically" },
  {
    k: "Click events",
    v: "Country derived first; IP truncated to /24 (IPv4) or /48 (IPv6) before durable storage",
  },
  { k: "Deleted links", v: "Removed from routing on delete; API slugs stay reserved 30 days" },
  { k: "Click history", v: "Kept at least your plan's window — nothing is purged early" },
  { k: "API keys", v: "Stored as SHA-256 hashes — the key is shown once, at creation" },
];

export const APPSEC = [
  { k: "auth", fact: "30-minute access JWTs · refresh issues a fresh token pair" },
  {
    k: "data layer",
    fact: "every query ships WHERE tenant_id = $ctx — enforced by the repository, not by review",
  },
  { k: "api keys", fact: "SHA-256 hashed at rest · plaintext shown once · revocable" },
];

/** Every row is live code (#395): no roadmap row until lib/roadmap.ts carries
 *  a dated entry for it (spec rule 3). The "Roadmap" badge style stays in
 *  AbuseSection for when one does. */
export const ABUSE: { name: string; desc: string; badge: "Live" | "Roadmap" }[] = [
  {
    name: "Bot protection & filtering",
    desc: "Write and form abuse controls run before acceptance; known crawlers, preview agents and uptime monitors are classified after redirects and excluded from customer analytics.",
    badge: "Live",
  },
  {
    name: "Rate limiting",
    desc: "Per API key, per IP for unauthenticated calls — 60 req/min on Free, 600 on Premium.",
    badge: "Live",
  },
  {
    name: "Malicious-URL scanning",
    desc: "Destinations are checked against Google Web Risk when a link is created or edited. Flagged URLs are refused; anonymous links and links on rii.link's shared domains are refused while the check can't run.",
    badge: "Live",
  },
  {
    name: "Destination blocklist",
    desc: "URLs and domains we block are refused when a link is written, and routing won't redirect to them.",
    badge: "Live",
  },
  {
    name: "Takedown",
    desc: "An operator can take a link down; routing stops serving it immediately.",
    badge: "Live",
  },
  {
    name: "Abuse reports",
    desc: "Anyone can report a link without an account. Reports go to a person on our team for review.",
    badge: "Live",
  },
];

/** Handoff prop `soc2Status` — switch only when a report exists; every copy
 *  variant lives in SOC2_COPY so the swap is one constant. Owner (2026-09-27):
 *  no SOC 2 attestation is held or under way to a date. */
export const SOC2_STATUS: "not-certified" | "certified" = "not-certified";

export const SOC2_COPY = {
  "not-certified": {
    badge: "Not certified",
    body: "We hold no SOC 2 report today and don't claim a date for one. Ask for our security documentation instead.",
  },
  certified: {
    badge: "Certified",
    body: "Type II report available under NDA — request it from sales@rii.link with your questionnaire.",
  },
} as const;

/** Derived from lib/legal/facts.ts — the same list the privacy policy
 *  publishes, so the two can't drift. Countries live in the policy. */
export const SUBPROCESSORS = legal.subprocessors.map((p) => ({
  key: `${p.name}:${p.purposeKey}`,
  name: p.name,
  purpose: PURPOSE[p.purposeKey],
  transfer: p.transferBasis === "scc" ? "SCCs" : "—",
}));

export const SUBPROCESSORS_FOOTNOTE =
  "ip-to-country lookups use a locally hosted database — ip addresses are not sent to a third party. " +
  "countries per processor: privacy policy §7.";

/** Mirrored 1:1 by `public/.well-known/security.txt` — the pane is a preview
 *  of the real file, so a change here must ship there too. */
export const SECURITY_TXT = [
  { k: "Contact", v: "mailto:security@rii.link" },
  { k: "Expires", v: "2027-08-09T00:00:00.000Z" },
  { k: "Encryption", v: "https://rii.link/.well-known/pgp-key.txt" },
  { k: "Preferred-Languages", v: "en, de" },
  { k: "Canonical", v: "https://rii.link/.well-known/security.txt" },
  { k: "Policy", v: "https://rii.link/security#disclosure" },
];

export const DISCLOSURE_CHECKS = [
  "Reports acknowledged within 72 hours — by an engineer, not an autoresponder",
  "Good-faith research is safe here — no legal threats for responsible reports",
];
